import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseUpsertComponent } from '@cl/core/components';
import { ClassifierService } from '@cl/core/services';
import { SinvoicesService } from '@cl/core/services/sinvoices.service';
import { AsyncValidatorService } from '@cl/core/validators/async-validator.service';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { Classifier, ClassifierType, EntityType, ErrorCode } from '@cl/shared/models';
import { Sinvoice } from '@cl/shared/models/sinvoices/sinvoice.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { filter, Observable, take } from 'rxjs';
import { InvoiceSumFieldsComponent } from '../invoice-sum-fields/invoice-sum-fields.component';

@Component({
    selector: 'cl-upsert-sinvoice',
    standalone: false,
    templateUrl: './upsert-sinvoice.component.html',
    styleUrl: './upsert-sinvoice.component.less',
})
export class UpsertSinvoiceComponent extends BaseUpsertComponent<Sinvoice, SinvoicesService> implements OnInit {
    EntityType = EntityType;
    GroupPermission = GroupPermissionsType;
    invoiceTypes$: Observable<Classifier[]>;

    sinvoiceId: number;

    fromSinvoices: boolean;

    @ViewChild('invoiceSumFields', { static: false }) invoiceSumFieldsComponent: InvoiceSumFieldsComponent;

    constructor(
        protected sinvoicesService: SinvoicesService,
        protected messageService: NzMessageService,
        private classifiersService: ClassifierService,
        private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private modalService: NzModalService,
        private asyncValidatorService: AsyncValidatorService,
    ) {
        super(sinvoicesService, messageService);

        // set modal texts
        this.saveSuccessText = 'Virsrēķins saglabāts';
        this.createObjectSaveErrorText = 'Kļūda pievienojot virsrēķinu';
        this.updateObjectSaveErrorText = 'Kļūda labojot virsrēķinu';
    }

    async ngOnInit(): Promise<void> {
        this.loading = true;
        this.invoiceTypes$ = this.classifiersService.getClassifiers(ClassifierType.InvoiceType);

        this.fromSinvoices = this.activatedRoute.snapshot.data['fromSinvoices'];
        this.sinvoiceId = this.activatedRoute.snapshot.params['id'];

        await this.reloadData();
        this.loading = false;
    }

    createForm(): UntypedFormGroup {
        return this.fb.group({
            clientId: [null, [Validators.required]],
            invoiceTypeId: [null, [Validators.required]],
            number: [null, [Validators.required, Validators.maxLength(17)], [this.asyncValidatorService.sinvoiceNumberValidator(null, null)]],
            date: [new Date(), [Validators.required]],
            dateUntil: [null, [Validators.required]],
            serviceDate: [null],
            sumWithoutVat: [null, [Validators.required, Validators.max(999999999999.99)]],
            vatPercent: [null, [Validators.required]],
            sumVat: [null, [Validators.required]],
            penaltySumWithoutVat: [0],
            sumWithVat: [null],
            notes: [null, [Validators.maxLength(2000)]],
        });
    }

    createAndFillForm(obj: Sinvoice): UntypedFormGroup {
        return this.fb.group({
            clientId: [this.originalObject.clientId, [Validators.required]],
            invoiceTypeId: [this.originalObject.invoiceTypeId, [Validators.required]],
            number: [
                this.originalObject.number,
                [Validators.required, Validators.maxLength(17)],
                this.asyncValidatorService.sinvoiceNumberValidator(this.originalObject.number, this.originalObject.id),
            ],
            date: [this.originalObject.date, [Validators.required]],
            dateUntil: [this.originalObject.dateUntil, [Validators.required]],
            serviceDate: [this.originalObject.serviceDate],
            sumWithoutVat: [this.originalObject.sumWithoutVat ?? 0, [Validators.required, Validators.max(999999999999.99)]],
            vatPercent: [this.originalObject.vatPercent ?? 0, [Validators.required]],
            sumVat: [this.originalObject.sumVat ?? 0, [Validators.required]],
            penaltySumWithoutVat: [this.originalObject.penaltySumWithoutVat ?? 0],
            sumWithVat: [this.originalObject.sumWithVat ?? 0],
            notes: [this.originalObject.notes, [Validators.maxLength(2000)]],
        });
    }

    afterInit(): void {
        // do the number unique validation on init
        if (this.isEditMode) {
            this.objectForm.controls['number'].markAsDirty();
            this.objectForm.controls['number'].updateValueAndValidity();
        }
    }

    submitAndReturn(val: boolean | Sinvoice): void {
        this.router.navigate(
            this.fromSinvoices
                ? ['bookkeeper', 'sinvoices', 'edit-sinvoice', this.originalObject.id]
                : ['base-data', 'invoices', 'edit-sinvoice', this.originalObject.id],
        );
    }

    cancel(): void {
        if (this.isEditMode) {
            this.router.navigate(
                this.fromSinvoices
                    ? ['bookkeeper', 'sinvoices', 'view-sinvoice', this.originalObject.id]
                    : ['base-data', 'invoices', 'view-sinvoice', this.originalObject.id],
            );
        } else {
            this.router.navigate(this.fromSinvoices ? ['bookkeeper', 'sinvoices'] : ['base-data', 'invoices']);
        }
    }

    async save(): Promise<void> {
        const sinvoice: Sinvoice = <Sinvoice>this.objectForm.getRawValue();
        if (this.isEditMode) {
            sinvoice.invoices = this.originalObject.invoices;
        }

        await this.sinvoicesService.update(this.objectId, sinvoice);
    }

    async validateAndSave(): Promise<void> {
        const automaticCalculationsNeedToBeRestored: boolean = this.invoiceSumFieldsComponent.isAutomaticCalculationsEnabled;
        if (this.invoiceSumFieldsComponent.isAutomaticCalculationsEnabled) {
            this.invoiceSumFieldsComponent.isAutomaticCalculationsEnabled = false;
        }

        this.objectForm.statusChanges
            .pipe(
                filter((status) => status !== 'PENDING'),
                take(1),
            )
            .subscribe(async (status) => {
                if (status !== 'VALID') {
                    return;
                }
                try {
                    this.loading = true;
                    if (this.returnObject) {
                        const obj = this.objectForm.getRawValue();
                        obj.id = this.objectId;
                        this.submitAndReturn(obj);
                        return;
                    }
                    await this.save();
                    this.messageService.success(this.saveSuccessText);
                    await this.reloadData();
                    this.submitAndReturn(true);

                    this.loading = false;
                    return;
                } catch (ex) {
                    this.loading = false;
                    if (ex.error?.code === ErrorCode.FieldValidation) {
                        this.fieldErrors = ex.error.errors;
                        this.mapErrorsToControls(ex.error.errors);
                    }

                    if (ex.error?.errors) {
                        ex.error.errors.forEach((error) => {
                            this.messageService.error(error);
                        });
                        return;
                    }

                    this.messageService.error(this.isEditMode ? this.updateObjectSaveErrorText : this.createObjectSaveErrorText);
                }
            });

        this.objectForm.markAsDirtyAndUpdateAll();

        if (automaticCalculationsNeedToBeRestored) {
            this.invoiceSumFieldsComponent.isAutomaticCalculationsEnabled = true;
        }
    }

    async reloadData(): Promise<void> {
        try {
            this.loading = true;
            this.originalObject = (
                await this.sinvoicesService.getInvoiceDetails(this.originalObject ? this.originalObject.id : this.sinvoiceId)
            ).data;
            this.setEditMode(this.originalObject);
            if (this.invoiceSumFieldsComponent) {
                this.invoiceSumFieldsComponent.sumWithVatBeforePenalty = Number(
                    (this.originalObject.sumWithoutVat + this.originalObject.sumVat).toFixed(2),
                );
            }
            this.loading = false;
            if (!this.originalObject) {
                this.messageService.error('Virsrēķins nav atrasts');
                this.router.navigate(this.fromSinvoices ? ['bookkeeper', 'sinvoices'] : ['base-data', 'invoices']);
                return;
            }
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                this.router.navigate(this.fromSinvoices ? ['bookkeeper', 'sinvoices'] : ['base-data', 'invoices']);

                return;
            }

            this.messageService.error('Kļūda atlasot virsrēķina informāciju');
            this.router.navigate(this.fromSinvoices ? ['bookkeeper', 'sinvoices'] : ['base-data', 'invoices']);

            return;
        }
    }

    async deleteSinvoice(): Promise<void> {
        if (this.originalObject?.invoices.length > 0) {
            this.messageService.error('Lai dzēstu virsrēķinu, nepieciešams noņemt saistītos apakšrēķinus!');
            return;
        }

        await this.modalService.confirm({
            nzTitle: 'Vai tiešām vēlaties dzēst virsrēķinu?',
            nzCancelText: 'Atcelt',
            nzOnOk: async (): Promise<void> => {
                try {
                    this.loading = true;
                    await this.sinvoicesService.delete(this.originalObject.id);
                    this.messageService.success('Virsrēķins veiksmīgi dzēsts');
                    this.loading = false;
                    this.router.navigate(this.fromSinvoices ? ['bookkeeper', 'sinvoices'] : ['base-data', 'invoices']);
                } catch (ex) {
                    this.loading = false;
                    console.error(ex);
                    if (ex.error?.errors) {
                        ex.error.errors.forEach((error) => {
                            this.messageService.error(error);
                        });
                        return;
                    }

                    this.messageService.error('Kļūda dzēšot virsrēķinu');
                    this.router.navigate(this.fromSinvoices ? ['bookkeeper', 'sinvoices'] : ['base-data', 'invoices']);
                    return;
                }
            },
        });
    }

    async restoreFromDeleted(): Promise<void> {
        if (!this.originalObject.isDeleted) {
            return;
        }

        await this.modalService.confirm({
            nzTitle: 'Vai tiešām izņemt virsrēķinu no dzēstajiem?',
            nzCancelText: 'Atcelt',
            nzOnOk: async (): Promise<void> => {
                try {
                    this.loading = true;
                    await this.sinvoicesService.restoreFromDeleted(this.originalObject.id);
                    await this.reloadData();
                    this.messageService.success('Virsrēķins veiksmīgi izņemts no dzēstajiem');
                    this.loading = false;
                } catch (ex) {
                    this.loading = false;
                    console.error(ex);
                    if (ex.error?.errors) {
                        ex.error.errors.forEach((error) => {
                            this.messageService.error(error);
                        });
                        return;
                    }

                    this.messageService.error('Kļūda izņemot virsrēķinu no dzēstajiem');
                    return;
                }
            },
        });
    }
}
